import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck } from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import Footer from '@/components/Footer';

// Feature hooks
import { usePaymentState } from './hooks/usePaymentState';
import { useOrderSummary } from './hooks/useOrderSummary';
import { useCoupon } from './hooks/useCoupon';
import { usePaymentForm } from './hooks/usePaymentForm';

// Feature components
import { PaymentMethodSelector } from './components/PaymentMethodSelector';
import { CardPaymentForm } from './components/CardPaymentForm';
import { UpiPaymentForm } from './components/UpiPaymentForm';
import { NetBankingForm } from './components/NetBankingForm';
import { WalletForm } from './components/WalletForm';
import { SavedMethodsList } from './components/SavedMethodsList';
import { OrderSummary } from './components/OrderSummary';
import { PaymentStatusOverlay } from './components/PaymentStatusOverlay';
import { PAYMENT_METHODS } from './types/index';

// ---------------------------------------------------------------------------
// Demo order items — in a real app these come from route state / cart context
// ---------------------------------------------------------------------------
const DEMO_ORDER_ITEMS = [
    { id: 'plan-pro', name: 'Pro Plan — Monthly', quantity: 1, price: 29.99 },
    { id: 'addon-api', name: 'API Access Add-on', quantity: 1, price: 9.99 },
];

/**
 * PaymentPortal — main page component.
 * Two-column layout on desktop, stacked on mobile.
 * Integrates all payment hooks and components.
 */
export function PaymentPortal() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, token } = useAuth();
    const { isDark } = useTheme();

    // Prefer items from router location state (passed from Pricing.js), fallback to demo
    const orderItems = location.state?.orderItems ?? DEMO_ORDER_ITEMS;

    // -------------------------------------------------------------------------
    // State layers
    // -------------------------------------------------------------------------
    const paymentState = usePaymentState();
    const orderSummary = useOrderSummary(orderItems);
    const coupon = useCoupon(token, orderSummary.subtotal, orderSummary.applyDiscount, orderSummary.removeDiscount);
    const paymentForm = usePaymentForm(token, paymentState, {
        orderId: location.state?.orderId ?? 'demo-order',
        amount: orderSummary.total,
        couponCode: orderSummary.couponCode,
    });

    // -------------------------------------------------------------------------
    // Render helpers — no business logic in JSX
    // -------------------------------------------------------------------------

    /**
     * Renders the correct form for the currently selected payment method.
     * @param {string} method
     */
    function renderMethodForm(method) {
        const commonProps = {
            onSubmit: paymentForm.form.handleSubmit(paymentForm.onSubmit),
            isProcessing: paymentState.isProcessing,
        };

        switch (method) {
            case PAYMENT_METHODS.CARD:
                return (
                    <CardPaymentForm
                        form={paymentForm.form}
                        onSubmit={paymentForm.onSubmit}
                        isProcessing={paymentState.isProcessing}
                    />
                );
            case PAYMENT_METHODS.UPI:
                return <UpiPaymentForm {...commonProps} />;
            case PAYMENT_METHODS.NET_BANKING:
                return <NetBankingForm {...commonProps} />;
            case PAYMENT_METHODS.WALLET:
                return <WalletForm {...commonProps} />;
            case PAYMENT_METHODS.SAVED:
                return (
                    <SavedMethodsList
                        token={token}
                        onSubmit={paymentForm.onSubmit}
                        isProcessing={paymentState.isProcessing}
                    />
                );
            default:
                return null;
        }
    }

    const paymentRightContent = (
        <>
            <div className="hidden md:flex items-center gap-2 text-sm mr-4">
                <ShieldCheck className="w-4 h-4 text-[#F87217]" aria-hidden="true" />
                <span className="font-medium" style={{ color: isDark ? '#c9d1d9' : '#475569' }}>Secure Checkout</span>
            </div>
            {isAuthenticated && (
                <Button
                    variant="ghost"
                    onClick={() => navigate('/dashboard')}
                    className="font-semibold rounded-xl"
                    style={{ color: isDark ? '#c9d1d9' : '#475569' }}
                    data-testid="nav-dashboard"
                >
                    Dashboard
                </Button>
            )}
        </>
    );

    return (
        <div
            className="min-h-screen page-enter transition-colors duration-300"
            style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}
            data-testid="payment-portal"
        >
            <AppHeader rightContent={paymentRightContent} />

            {/* Page Header */}
            <header className="pt-10 pb-6 px-6">
                <div className="max-w-5xl mx-auto">
                    <h1
                        className="text-3xl font-bold font-['Outfit'] transition-colors duration-300"
                        style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
                    >
                        Complete Your Purchase
                    </h1>
                    <p
                        className="mt-1 text-sm transition-colors duration-300"
                        style={{ color: isDark ? '#8b949e' : '#64748b' }}
                    >
                        Choose a payment method and review your order below.
                    </p>
                </div>
            </header>

            {/* Main Content — two-column layout */}
            <main className="pb-20 px-6">
                <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">

                    {/* ---------------------------------------------------------------- */}
                    {/* LEFT: Payment Method Selector + Active Form                       */}
                    {/* ---------------------------------------------------------------- */}
                    <Card className="border-slate-200 rounded-sm shadow-sm h-fit" data-testid="payment-form-panel">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg font-bold text-slate-900 font-['Outfit']">
                                Payment Method
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <PaymentMethodSelector
                                selectedMethod={paymentForm.selectedMethod}
                                onMethodChange={paymentForm.setSelectedMethod}
                            >
                                {(method) => renderMethodForm(method)}
                            </PaymentMethodSelector>
                        </CardContent>
                    </Card>

                    {/* ---------------------------------------------------------------- */}
                    {/* RIGHT: Order Summary                                              */}
                    {/* ---------------------------------------------------------------- */}
                    <aside aria-label="Order summary" className="h-fit">
                        <OrderSummary
                            items={orderSummary.items}
                            subtotal={orderSummary.subtotal}
                            tax={orderSummary.tax}
                            discount={orderSummary.discount}
                            total={orderSummary.total}
                            couponCode={orderSummary.couponCode}
                            couponLabel={orderSummary.couponLabel}
                            onApplyCoupon={coupon.handleApply}
                            onRemoveCoupon={coupon.handleRemove}
                            isCouponLoading={coupon.isLoading}
                        />
                    </aside>
                </div>
            </main>

            <Footer />

            {/* -------------------------------------------------------------------- */}
            {/* Payment Status Overlay (processing / success / failed / cancelled)   */}
            {/* -------------------------------------------------------------------- */}
            <PaymentStatusOverlay
                status={paymentState.status}
                errorMessage={paymentState.errorMessage}
                retryCount={paymentState.retryCount}
                onRetry={paymentState.retry}
                onCancel={paymentState.setCancelled}
                onReset={paymentState.reset}
            />
        </div>
    );
}

export default PaymentPortal;
